import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
    UserSession,
    TeamsList,
    CurrentTeam,
    setDefaultData,
    sanitize,
    kebabToTitle
  } from '../utils'
import { Client, Account, Databases, Functions } from "appwrite";
import { getWidgetData, updateWidgetData } from '../appwrite/methods'
import InfoTooltip from './InfoTooltip'

import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';




// const client = new Client()
//     .setEndpoint(process.env.REACT_APP_ENDPOINT)
//     .setProject(process.env.REACT_APP_PROJECT_ID);

// const account = new Account(client);
// const db = new Databases(client);
// const functions = new Functions(client);


/**
 * @Multiselect
 * 
 * @param {Array<String>} options Required. Default array of items to populate the multiselect with. If `defaultButton` exists, this is also what the multiselect will be reset to.
 * @param {String} widgetName Optional. The name of the widget to save data to. If unspecified, data will only be saved to cache.
 * @param {String} docKey Optional. The key to store selected items by in the user's database. If unspecified, will only be saved to cache (reset on page reload).
 * @param {String} header Optional. Header text for the multiselect. If unspecified, no header will be shown.
 * @param {Boolean} selectable Optional. Whether items in the multiselect can be toggled on/off while still being visible as options. If true, only selected elements will be returned. Default is false.
 * @param {Boolean} isMulti Optional. Whether to show a button to deselect all currently selected items. Should not be present if `selectable` is false. Default is false.
 * @param {Boolean} removeItems Optional. Whether to show a deletebutton to remove items from the multiselect. Default is true.
 * @param {Boolean} addKeywords Optional. Whether to show an input field to add new keywords. Default is true.
 * @param {Boolean} resetButton Optional. Whether to show a default button to reset the multiselect. Default is true.
 * @param {String} inputText Optional. Example text for the input field. Default is 'New item'.
 * @param {String} resetButtonText Optional. Text for the default button. Default is 'Reset to Defaults'.
 * @param {Boolean} clearAll Optional. Whether to show a button to clear all items. Default is false.
 * 
 * @returns {JSX.Element} Rendered Multiselect component.
*/
/**
 * @TODO
 * - Add selection items toggle
 * - Confirm resetting prompt
 * - Get save to localStorage working for guest sessions (currently docKey is a required field)
 * - Add clear all button
 * - Add deselect all button
 */
export default function Multiselect({ options, widgetName, docKey, header, selectable, isMulti, removeItems, addKeywords, resetButton, inputText, resetButtonText, clearAll, tooltip, tooltipIcon }) {
    const [userSession, setUserSession] = useAtom(UserSession);
    const [teamsList, setTeamsList] = useAtom(TeamsList);
    const [currentTeam, setCurrentTeam] = useAtom(CurrentTeam);
    
    const [keywords, setKeywords] = useState([]);


    // Get existing data, or fallback to default options
    useEffect(() => {
        if (teamsList.length > 0) {
            const widgetData = getWidgetData(
                sanitize(teamsList[currentTeam]),
                userSession.$id,
                kebabToTitle(widgetName),
                docKey
            ).then(data => {
                if (data.length > 0) setKeywords(data);
            })

            if (widgetData.length === undefined || widgetData.length < 1) {
                setKeywords(options);
            }
        }
    }, [teamsList]);

    function update(value, index) {
        let updatedValue
        switch (value) {
            // Deleting
            case null:
                updatedValue = keywords.toSpliced(index, 1)
                break
            // Resetting
            case -1:
                updatedValue = options
                break
            // Adding
            default:
                if (keywords.includes(value)) {
                    // TODO: this needs to be a toast
                    return
                } else {
                    updatedValue = keywords.concat([value])
                }
        }

        setKeywords(updatedValue || []);
        updateWidgetData(
            sanitize(teamsList[currentTeam]),
            userSession.$id,
            kebabToTitle(widgetName),
            { [docKey]: updatedValue },
            {
                [docKey]: {
                    type: 'string',
                    array: true
                }
            }
        )
    }


    return (<>
        <Stack direction="row" spacing={1}>
            {header && <Typography variant="h6" gutterBottom>{header}</Typography>}
            {tooltip && <Stack><InfoTooltip text={tooltip} icon={tooltipIcon} /></Stack>}
        </Stack>
        <Stack direction="row" spacing={0.1} flexWrap="wrap" sx={{ gridGap: '0.25em' }}>
            {keywords.length > 0 && keywords.map((keyword, index) => (
                <Chip
                    key={index}
                    label={keyword}
                    variant="outlined"
                    onDelete={removeItems !== false ? () => update(null, index) : false}
                    sx={{ marginBottom: 1 }}
                />
            ))}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ display: 'flex', alignItems: 'center', mt: '0.5em' }}>
            {addKeywords !== false && <Stack
                component="form"
                direction="row"
                noValidate
                autoComplete="off"
                onSubmit={(e) => {
                    e.preventDefault();
                    update(document.getElementById("new-item").value)
                    document.getElementById("new-item").value = null
                }}
                spacing={1}
                sx={{ display: 'flex', alignItems: 'center' }}
            >
                <TextField
                    id="new-item"
                    label={inputText ?? "New item"}
                    variant="outlined"
                    size="small"
                    sx={{
                        '& .MuiInputBase-input': { height: '2.4em' }
                    }}
                />
                <Box>
                    <Button variant="contained" type="submit">Add</Button>
                </Box>
            </Stack>}
            {resetButton !== false && <Box>
                <Button onClick={() => {update(-1)}}>
                    {resetButtonText ?? 'Reset to Defaults'}
                </Button>
            </Box>}
        </Stack>
    </>)
}